/**
 * ConvertedQuoteGuard — IDI-157 (BUILD CONTRACT 2026-10-21).
 *
 * ONE route-level guard on /quotes/:id, /edit, /review and /record-decision
 * (09-30 ruling: one central guard, not per caller). A converted quote is a
 * job (Adrian 09-02), so it has no quote-shaped page to land on.
 *
 * "Converted" = convertedJobId / converted_job_id, whatever the status —
 * lost-but-converted redirects too (Adrian 10-20). No id → no redirect.
 *
 * Nothing in the wrapped page mounts until the quote has been read. That is
 * what stops the builder from painting (and from firing its autosave PUT) on
 * a converted quote: the page is simply not in the tree yet. While loading,
 * only a neutral loader shows.
 *
 * The redirect uses replace, so browser Back does not bounce back into the
 * guard, and carries the origin state forward so Back on the job still
 * returns to the list the user came from.
 *
 * If the read fails, the wrapped page renders and owns its own error state —
 * the guard never hides a quote it could not classify.
 *
 * IDI-564: /quotes/add is wrapped too (no id → straight through). When the
 * builder's first autosave navigates add → /quotes/<id>/edit it passes
 * state.justCreatedQuoteId; a guard instance that was itself mounted on
 * /quotes/add trusts that marker during render, so it never swaps the builder
 * for its loader (which would unmount it) and does not fetch — a quote
 * created a moment ago cannot be converted. A fresh mount (reload, Back,
 * deep link) ignores the marker and checks as usual.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageLoader } from '../ui/Spinner';
import api from '../../services/api';
import { convertedJobIdOf } from '../../utils/quoteRouting';
import { carryOrigin } from '../../utils/backOrigin';

export default function ConvertedQuoteGuard({ children }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Keyed by id so moving between two quotes re-checks before rendering.
  const [checked, setChecked] = useState(id ? null : '__none__');
  // IDI-564: only an instance that started on /quotes/add (no id) can be
  // holding the page that just created this quote.
  const mountedWithoutIdRef = useRef(!id);
  const justCreated = !!id && mountedWithoutIdRef.current
    && location.state?.justCreatedQuoteId === id;

  useEffect(() => {
    if (!id) { setChecked('__none__'); return undefined; }
    if (justCreated) { setChecked(id); return undefined; }
    let cancelled = false;
    setChecked(null);
    (async () => {
      let jobId = null;
      try {
        const res = await api.getQuote(id);
        jobId = convertedJobIdOf(res?.data || res);
      } catch {
        jobId = null;
      }
      if (cancelled) return;
      if (jobId) {
        navigate(`/jobs/${jobId}`, { replace: true, state: carryOrigin(location.state) });
        return;
      }
      setChecked(id);
    })();
    return () => { cancelled = true; };
    // location.state is read once at redirect time; re-running on it would refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const ready = justCreated || (id ? checked === id : checked === '__none__');
  if (!ready) {
    return (
      <div data-testid="converted-quote-guard-loading">
        <PageLoader />
      </div>
    );
  }
  return <>{children}</>;
}
